import { createFileRoute, redirect } from "@tanstack/react-router";
import { hasSessionHint, tryGetProfile } from "@core/auth";
import { ApplicantRegisterScreen } from "@modules/account/views";
import { RoundApplyContext } from "@modules/rounds/views";
import { ensurePublicRound } from "@modules/rounds/public-catalog";
import { setPendingRound } from "@modules/applications/_shared";

/**
 * `/apply/$roundId/register` — the applicant's entry to the apply funnel, reached from a round card
 * in the public catalogue at `/website`.
 *
 * PUBLIC. The trailing `_` on `apply_` escapes the `/apply/$roundId` layout chain (the same trick as
 * `staff_.rounds.create.tsx`), which matters: that parent is applicant-guarded, and inheriting its
 * guard would bounce every unregistered visitor to login — the exact people this screen exists for.
 *
 * The funnel is routed under `/apply` on purpose. The server's `safe_next_path` whitelists only
 * `/apply` and `/applicant` for `applicant_workspace`, so a top-level `/register` would be silently
 * dropped to `""` and the round would not survive login.
 *
 * The ROUTE is the composition point for the two modules involved: `account` owns the form, `rounds`
 * owns the round context panel, and modules may not import each other — so the panel is passed into
 * a slot the account screen exposes.
 */
export const Route = createFileRoute("/apply_/$roundId/register")({
  beforeLoad: async ({ context, params }) => {
    /**
     * Gate 1 — already signed in? Registration is meaningless, so go straight to the application.
     *
     * ⚠️ The probe is CONDITIONAL. Unguarded, `tryGetProfile` cost every anonymous visitor a
     * `GET /auth/me` 401 plus a `POST /auth/token/refresh/` 401 before the page could render —
     * measured on the real app — and on this funnel anonymous is the normal case.
     *
     * `hasSessionHint()` is false only when no session has ever existed in this browser, which is
     * exactly when asking is pointless. When it is true the probe runs as before, so a returning
     * applicant is still forwarded to their application rather than shown a form for an account they
     * already have.
     *
     * An in-memory token check was tried first and does NOT work here: a signed-in applicant
     * browsing `/website` and `/rounds/{id}` makes no authenticated request, so their token is empty
     * and they are indistinguishable from a stranger. The shortcut simply never fired.
     */
    if (hasSessionHint()) {
      const profile = await tryGetProfile(context.queryClient);
      if (profile) {
        throw redirect({ to: "/apply/$roundId", params });
      }
    }

    /**
     * Gate 2 — the round must exist, be publicly visible, and be open.
     *
     * This is a CONTEXT precondition, not access control: round ids are public (they ship in the
     * catalogue response), so anyone can forge one and this cannot protect anything. What it does
     * buy is that the screen never renders a form leading nowhere — an unknown, invisible or closed
     * round returns to the catalogue instead of collecting an account for an application that can
     * never be submitted.
     *
     * `ensurePublicRound` caches under the same key the screen's panel reads, so resolving here
     * costs no extra request.
     */
    // `ensurePublicRound` now resolves the round AND its applicant schema, since the preview screen
    // needs both from the same cache entry. Only the round matters to this guard.
    const preview = await ensurePublicRound(
      context.queryClient,
      params.roundId
    );
    if (!preview) {
      throw redirect({ to: "/website" });
    }
    // `accepting_applications`, not `status`: a round can be OPEN and still refuse new applications,
    // and the server computes that distinction so no client has to re-derive it.
    if (!preview.round.accepting_applications) {
      throw redirect({ to: "/website" });
    }
  },
  component: ApplicantRegisterRoute,
});

function ApplicantRegisterRoute() {
  const { roundId } = Route.useParams();

  return (
    <ApplicantRegisterScreen
      roundId={roundId}
      // Identity only. `/rounds/{roundId}` is now the step immediately before this one and shows
      // the eligibility conditions and the documents in full, so repeating them here would be the
      // same three cards twice in a row — with the account form, the only thing this screen asks
      // for, pushed below them.
      roundContext={
        <RoundApplyContext roundId={roundId} showRequirements={false} />
      }
      /*
        Park the round so it survives the verification email.
        The dossier is created once the address is confirmed, and confirmation lands on
        `/verify-email?token=…` — a URL the BACKEND builds, carrying a token and nothing else. This
        is the only way the round reaches that moment. See `pending-round.ts` for why the
        alternatives do not exist and what the limits are.
        Done in the ROUTE because it crosses a module boundary the screen may not: `account` cannot
        import `applications`. This route was already the composition point for `account` + `rounds`.
      */
      onRegistered={() => setPendingRound(roundId)}
    />
  );
}
